import React from 'react';
import {
  DocumentTextIcon,
  StarIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

export default function AppraisalFormHelp() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Appraisal Form Guide</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn how to create comprehensive performance appraisals with AI-powered insights and structured evaluation frameworks.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ChartBarIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Appraisal Form is your comprehensive tool for conducting employee performance reviews.
            It combines structured evaluation criteria with AI-powered training recommendations to create
            meaningful development plans and performance documentation.
          </p>
        </div>

        {/* Step-by-Step Process */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">How to Create an Appraisal</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Select Employee</h3>
              <p className="text-gray-600 text-sm">Choose the staff member and review period for evaluation</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Performance Evaluation</h3>
              <p className="text-gray-600 text-sm">Rate overall performance and document key strengths</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">AI Training Report</h3>
              <p className="text-gray-600 text-sm">Generate personalized training recommendations</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Submit & Review</h3>
              <p className="text-gray-600 text-sm">Complete the appraisal and send for employee feedback</p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Performance Evaluation */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <StarIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Performance Evaluation</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Review Period:</strong> Set start and end dates for the evaluation period</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Overall Rating:</strong> Rate performance on a 1-5 scale</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Key Strengths:</strong> Document employee's positive contributions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Areas for Improvement:</strong> Identify development opportunities</span>
              </li>
            </ul>
          </div>

          {/* Employee Development */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Employee Development</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Goal Achievement:</strong> Assess if performance goals were met</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Leadership Skills:</strong> Evaluate teamwork and leadership abilities</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Peer Feedback:</strong> Include colleague perspectives</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Development Goals:</strong> Set future improvement objectives</span>
              </li>
            </ul>
          </div>

          {/* Training & Development */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <AcademicCapIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Training & Development</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>AI Training Report:</strong> Generate personalized training recommendations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Recommended Courses:</strong> Select from available training programs</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Career Skills:</strong> Identify skills needed for advancement</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Compensation Updates:</strong> Document salary or benefit changes</span>
              </li>
            </ul>
          </div>

          {/* Overall Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Overall Summary</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Performance Summary:</strong> Comprehensive evaluation overview</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Role Changes:</strong> Document any responsibility modifications</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Action Plan:</strong> Outline next steps and follow-up actions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Author Tracking:</strong> Automatically records who created the appraisal</span>
              </li>
            </ul>
          </div>
        </div>

        {/* AI Features */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white mb-8">
          <div className="flex items-center mb-6">
            <Cog6ToothIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">AI-Powered Features</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Generate AI Training Report</h3>
              <p className="text-blue-100 mb-4">
                Click the "Generate AI Training Report" button to receive personalized training recommendations
                based on the employee's current performance data and development needs.
              </p>
              <ul className="space-y-2 text-blue-100">
                <li>• Analyzes employee performance patterns</li>
                <li>• Recommends specific training courses</li>
                <li>• Identifies skill gaps and development opportunities</li>
                <li>• Provides actionable improvement suggestions</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Smart Recommendations</h3>
              <p className="text-blue-100 mb-4">
                The AI analyzes multiple factors including performance ratings, areas for improvement,
                and career goals to suggest the most relevant training programs.
              </p>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm">
                  <strong>Pro Tip:</strong> Generate the AI report after completing the performance evaluation
                  section for more accurate recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Tips for Effective Appraisals</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 Documentation Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Be specific with examples and achievements</li>
                <li>• Use measurable criteria when possible</li>
                <li>• Balance positive feedback with constructive criticism</li>
                <li>• Include both quantitative and qualitative assessments</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 Development Focus</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Set SMART (Specific, Measurable, Achievable, Relevant, Time-bound) goals</li>
                <li>• Link development plans to business objectives</li>
                <li>• Consider both technical and soft skills development</li>
                <li>• Follow up regularly on progress and achievements</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💬 Communication</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Schedule appraisal meetings in advance</li>
                <li>• Prepare employees beforehand with self-assessment forms</li>
                <li>• Create a supportive environment for open dialogue</li>
                <li>• Document agreements and action items clearly</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use landscape mode on mobile for better form navigation</li>
                <li>• Save drafts frequently when using mobile devices</li>
                <li>• Review all sections before submitting on small screens</li>
                <li>• Consider using a tablet for extended appraisal sessions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}